import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const resolveAsset = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export const FONT_OPTIONS = [
  "Outfit", "Plus Jakarta Sans", "Inter", "Poppins", "Montserrat", "Roboto",
  "Lato", "Work Sans", "DM Sans", "Space Grotesk", "Manrope", "Nunito",
  "Raleway", "Sora", "Playfair Display", "Merriweather",
];

export const RADIUS_OPTIONS = [
  { value: "sharp", label: "Sharp", css: "0.15rem" },
  { value: "rounded", label: "Rounded", css: "0.65rem" },
  { value: "pill", label: "Pill", css: "1.25rem" },
];

export const HEIGHT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "compact", label: "Compact" },
  { value: "comfortable", label: "Comfortable" },
  { value: "large", label: "Large" },
];

export const BORDER_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "0", label: "None" },
  { value: "1", label: "Thin" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Thick" },
];

export const WEIGHT_OPTIONS = [
  { value: 300, label: "Light" },
  { value: 400, label: "Regular" },
  { value: 500, label: "Medium" },
  { value: 600, label: "Semibold" },
  { value: 700, label: "Bold" },
  { value: 800, label: "Extrabold" },
];

export const DEFAULT_BRANDING = {
  brand_name: "The Good Box",
  logo_scale: 100,
  logo_url: "", logo_light_url: "", favicon_url: "",
  colors: { primary: "#7C3AED", accent: "#c026d3", background: "#020617", surface: "#0f172a", text: "#ffffff" },
  gradient: { from: "#1e3a8a", to: "#0f172a", angle: 135 },
  kiosk_gradient: { from: "#4F46E5", to: "#9333EA", angle: 135 },
  fonts: { heading: "Outfit", subheading: "Outfit", body: "Plus Jakarta Sans", heading_weight: 700, body_weight: 400 },
  button: { radius: "rounded", height: "default", border_width: "default", border_color: "#e2e8f0" },
  field: { radius: "rounded", height: "default", border_width: "default", border_color: "#e2e8f0" },
  backgrounds: { login: "", kiosk: "" },
  hero: { title: "Run every kiosk from one place.", subtitle: "Manage products, stock and reporting across your estate." },
  bug_reporting: { enabled: true },
};

export function mergeBranding(stored) {
  const out = JSON.parse(JSON.stringify(DEFAULT_BRANDING));
  if (stored) {
    for (const k of Object.keys(out)) {
      if (stored[k] == null) continue;
      if (typeof out[k] === "object" && !Array.isArray(out[k])) out[k] = { ...out[k], ...stored[k] };
      else out[k] = stored[k];
    }
  }
  return out;
}

function darken(hex, amt = 0.12) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const f = 1 - amt;
  const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * f));
  const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * f));
  const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * f));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

function hexToHsl(hex) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function loadFonts(families) {
  const uniq = [...new Set(families.filter(Boolean))];
  const spec = uniq.map((f) => `family=${f.replace(/ /g, "+")}:wght@300;400;500;600;700;800`).join("&");
  const href = `https://fonts.googleapis.com/css2?${spec}&display=swap`;
  let link = document.getElementById("hv-brand-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "hv-brand-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

function setFavicon(url) {
  if (!url) return;
  // Remove any existing icon links so the browser can't keep serving the old (cached) one.
  document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((l) => l.parentNode && l.parentNode.removeChild(l));
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = url;
  document.head.appendChild(link);
  const apple = document.createElement("link");
  apple.rel = "apple-touch-icon";
  apple.href = url;
  document.head.appendChild(apple);
}

function setVar(root, key, val) {
  if (val === undefined || val === null || val === "") root.style.removeProperty(key);
  else root.style.setProperty(key, String(val));
}

const HEIGHT_CSS = { default: "", compact: "36px", comfortable: "44px", large: "52px" };

function ensureBrandStylesheet() {
  if (document.getElementById("hv-brand-style")) return;
  const el = document.createElement("style");
  el.id = "hv-brand-style";
  el.textContent =
    ".hv-btn{border-radius:var(--btn-radius);min-height:var(--btn-height);border-style:solid;border-width:var(--btn-border-width);border-color:var(--btn-border-color);}" +
    ".hv-field{border-radius:var(--field-radius);min-height:var(--field-height);border-width:var(--field-border-width);border-color:var(--field-border-color);}";
  document.head.appendChild(el);
}

function applyEdge(root, prefix, cfg) {
  root.style.setProperty(`${prefix}-radius`, (RADIUS_OPTIONS.find((r) => r.value === cfg.radius) || RADIUS_OPTIONS[1]).css);
  setVar(root, `${prefix}-height`, HEIGHT_CSS[cfg.height] ?? "");
  if (!cfg.border_width || cfg.border_width === "default") {
    setVar(root, `${prefix}-border-width`, "");
    setVar(root, `${prefix}-border-color`, "");
  } else {
    setVar(root, `${prefix}-border-width`, `${cfg.border_width}px`);
    setVar(root, `${prefix}-border-color`, cfg.border_color || "#e2e8f0");
  }
}

export function applyBranding(b) {
  const root = document.documentElement;
  ensureBrandStylesheet();
  const c = b.colors || {};
  root.style.setProperty("--brand-primary", c.primary);
  root.style.setProperty("--brand-accent", c.accent);
  root.style.setProperty("--brand-bg", c.background);
  root.style.setProperty("--brand-surface", c.surface);
  root.style.setProperty("--brand-text", c.text);
  const g = b.gradient || {};
  root.style.setProperty("--brand-gradient", `linear-gradient(${g.angle ?? 135}deg, ${g.from}, ${g.to})`);
  const kg = b.kiosk_gradient || {};
  root.style.setProperty("--brand-kiosk-gradient", `linear-gradient(${kg.angle ?? 135}deg, ${kg.from}, ${kg.to})`);
  const ls = Math.max(0.5, Math.min(1.6, (Number(b.logo_scale) || 100) / 100));
  root.style.setProperty("--brand-logo-scale", String(ls));
  try { root.style.setProperty("--brand-primary-hover", darken(c.primary, 0.12)); } catch { /* ignore */ }
  root.style.setProperty("--radius", (RADIUS_OPTIONS.find((r) => r.value === b.button?.radius) || RADIUS_OPTIONS[1]).css);
  const f = b.fonts || {};
  root.style.setProperty("--font-head", `'${f.heading}'`);
  root.style.setProperty("--font-sub", `'${f.subheading}'`);
  root.style.setProperty("--font-body", `'${f.body}'`);
  root.style.setProperty("--font-head-weight", String(f.heading_weight || 700));
  root.style.setProperty("--font-body-weight", String(f.body_weight || 400));
  applyEdge(root, "--btn", b.button || {});
  applyEdge(root, "--field", b.field || {});
  try {
    const hsl = hexToHsl(c.primary);
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--ring", hsl);
  } catch { /* ignore bad hex */ }
  loadFonts([f.heading, f.subheading, f.body]);
  if (b.favicon_url) setFavicon(resolveAsset(b.favicon_url));
  if (b.brand_name) document.title = b.brand_name;
}

const BrandingContext = createContext({ branding: DEFAULT_BRANDING, setBranding: () => {} });

export function BrandingProvider({ children }) {
  const [branding, setBrandingState] = useState(DEFAULT_BRANDING);
  useEffect(() => {
    api.get("/settings/branding")
      .then((r) => { const m = mergeBranding(r.data); setBrandingState(m); applyBranding(m); })
      .catch(() => applyBranding(DEFAULT_BRANDING));
  }, []);
  const setBranding = (b) => { const m = mergeBranding(b); setBrandingState(m); applyBranding(m); };
  return <BrandingContext.Provider value={{ branding, setBranding }}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
